import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, CUSTOM_ELEMENTS_SCHEMA,
  PLATFORM_ID, QueryList, Renderer2, signal, SimpleChanges, TemplateRef, viewChild, ViewEncapsulation,
  ElementRef, HostBinding, Inject, Input, OnChanges, OnDestroy, OnInit, output
} from '@angular/core';

import { isPlatformBrowser, NgClass, NgTemplateOutlet } from '@angular/common';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MediaMatcher } from '@angular/cdk/layout';
import { fromEvent, Subscription } from 'rxjs';
import { v4 as uuid } from 'uuid';

import SwiperCore, { Navigation, Pagination, Swiper, SwiperOptions } from 'swiper';
import { NavigationOptions } from 'swiper/types';
import { SwiperModule } from 'swiper/angular';

import {
  BrandsEnum, DeviceService, ISliderInfo, mBreakpoint, SdBannerComponent, SdButtonComponent, SizesEnum
} from '@seb/web-design-system/core';

import { SliderUtils } from '../slider-a11y.utils';

SwiperCore.use([Navigation, Pagination]);

enum BRK {
  S = '0',
  M = '768',
  L = '1280'
}

@Component({
  selector: 'sd-slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgClass,
    SwiperModule,
    NgTemplateOutlet,
    SdButtonComponent,
    TranslateModule
  ]
})
export class SdSliderComponent implements OnChanges, OnInit, OnDestroy, AfterViewInit {
  @Input()
  brand: BrandsEnum;

  /**
   * See "media" child inside slides, it allows to manage play/pause on
   * media on slide transition
   */
  @Input()
  mediaBanners: QueryList<SdBannerComponent>;

  @Input()
  customConfig: SwiperOptions;

  @Input()
  context = 'Slider';

  @Input()
  isFullWidth = false;

  @Input()
  isQuote = false;

  @Input()
  itemTemplate: TemplateRef<any>;

  @Input()
  items: any[];

  @Input()
  size: SizesEnum;

  @Input()
  noStyle = false;

  /**
   * Emit on "window resize"
   */
  resizeCallback = output<void>();

  /**
   * Emit on click "next slide"
   */
  slideNext = output<ISliderInfo>();

  /**
   * Emit on click "previous slide"
   */
  slidePrev = output<ISliderInfo>();

  /**
   * Emit swiper informations after initialization
   */
  swiperInfo = output<ISliderInfo>();

  slideChange = output<number>();

  /**
   * Emit on "Image ready"
   */
  imagesReadyCallback = output<void>();

  @Input() class = '';

  @HostBinding('class')
  get hostClasses(): string {
    return [
      this.class,
      this.size ? 'is-size-' + this.size : '',
      this.isFullWidth ? 'is-full-width is-slider' : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  breakpoint: MediaQueryList;
  navigation = signal<NavigationOptions>(undefined);
  configDefault = signal<SwiperOptions>(undefined);
  subscriptions = new Subscription();
  sliderUtils: SliderUtils;
  slideText = signal<string>('');
  uuidSlider = uuid();
  isPrevBtnDisabled = signal(true);
  isNextBtnDisabled = signal(false);

  private isMobile = signal(false);
  private isTablet = signal(false);

  private readonly swiperContainer = viewChild.required('swiperContainer', { read: ElementRef });
  private readonly buttonPrev = viewChild.required('buttonPrev', { read: ElementRef });
  private readonly buttonNext = viewChild.required('buttonNext', { read: ElementRef });

  private readonly swiper = computed(() => this.swiperContainer().nativeElement?.swiper as Swiper);

  private readonly slidesPerView = computed(() => {
    const breakpoints = this.configDefault()?.breakpoints ?? this.swiper()?.params.breakpoints;
    const index = this.isMobile() ? BRK.S : this.isTablet ? BRK.M : BRK.L;

    return breakpoints[index]?.slidesPerView || 'auto';
  });

  get activeIndex(): number {
    return this.swiper()?.activeIndex ?? 0;
  }

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private cdRef: ChangeDetectorRef,
    private mediaMatcher: MediaMatcher,
    private deviceService: DeviceService,
    private translateService: TranslateService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.sliderUtils = new SliderUtils(
      this.renderer,
      this.cdRef,
      this.translateService,
      this.subscriptions
    );
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.breakpoint = this.mediaMatcher.matchMedia(
        '(max-width: ' + (mBreakpoint - 1) + 'px)'
      );

      this.subscriptions.add(
        fromEvent(this.breakpoint, 'change').subscribe(() => {
          this.updateSlidesStatus();
        })
      );

      this.subscriptions.add(
        this.translateService.get('sliders.a11y.visual').subscribe((text: string) => {
          this.slideText.set(text);
        })
      );
    }

    this.subscriptions.add(
      this.deviceService.isMobile.subscribe((x: boolean) => {
        this.isMobile.set(x);
      })
    );

    this.subscriptions.add(
      this.deviceService.isTablet.subscribe((x: boolean) => {
        this.isTablet.set(x);
      })
    );
  }

  ngAfterViewInit(): void {
    this.updateSliderConfig();

    if (isPlatformBrowser(this.platformId)) {
      this.slideChange.emit(this.activeIndex);
      /*
       * A11y
       */
      setTimeout(() => {
        this.sliderUtils.setSliderA11y(this.context, this.mediaBanners);
      }, 500);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (isPlatformBrowser(this.platformId)) {
      if (changes['items'] && !changes['items'].isFirstChange()) {
        /*
         * A11y
         */
        setTimeout(() => {
          this.sliderUtils.setSliderA11y(this.context, this.mediaBanners);
        }, 500);
      }

      if (changes['size'] && !changes['size'].isFirstChange()) {
        this.updateSliderConfig();
      }
    }
  }

  onSliderInit(): void {
    if (this.mediaBanners) {
      this.mediaBanners.forEach((media: SdBannerComponent, index: number) => {
        if (index !== this.activeIndex) {
          media.pauseMedia();
        }
      });
    }
  }

  /**
   * Force focus on active slide
   * (will probably need rework after a11Y feedback)
   */
  updateSlidesStatus(): void {
    this.sliderUtils.updateSlides(
      this.el,
      this.mediaBanners,
      this.swiperContainer()
    );
  }

  onSlideNext(): void {
    this.slideNext.emit({
      index: this.activeIndex,
      itemsPerView: this.slidesPerView()
    });
    this.updateButtons();
  }

  onSlidePrev(): void {
    this.slidePrev.emit({
      index: this.activeIndex,
      itemsPerView: this.slidesPerView()
    });
    this.updateButtons();
  }

  onImageReady(): void {
    this.imagesReadyCallback.emit();
  }

  onResize(): boolean {
    this.resizeCallback.emit();
    return true;
  }

  refreshSlider(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.swiper()?.update();
    }
  }

  goToSlide(index: number): void {
    if (index !== this.activeIndex) {
      this.swiper()?.slideTo(index);
    }
  }

  /**
   * Build or update Swiper configuration objects
   */
  private updateSliderConfig(): void {
    const [ngSlidesL, nbSlidesS, nbSlidesXS] = this.getSliderSize(this.size);
    const config = this.customConfig || {};

    if (this.navigation() === undefined) {
      this.navigation.set({
        prevEl: this.buttonPrev().nativeElement,
        nextEl: this.buttonNext().nativeElement,
        disabledClass: 'is-disabled'
      });
    }

    this.configDefault.set({
      init: config.init || true,
      wrapperClass: config.wrapperClass || 'c__items',
      slideDuplicateClass: 'swiper-slide-duplicate',
      hashNavigation: true,
      effect: config.effect || 'slide',
      zoom: config.zoom || false,
      cssMode: false,
      autoHeight: config.autoHeight || false,
      direction: 'horizontal',
      observer: config.observer || true,
      spaceBetween: config.spaceBetween || 0,
      centerInsufficientSlides: config.centerInsufficientSlides || true,
      slideClass: 'c__item',
      speed: config.speed || 600,
      watchOverflow: config.watchOverflow || true,
      watchSlidesProgress: config.watchSlidesProgress || true,
      centeredSlides: config.centeredSlides || false,
      parallax: config.parallax || false,
      pagination: config.pagination ?? false,
      slidesPerView: ngSlidesL || 3,
      slidesPerGroup: ngSlidesL || 3,
      loop: config.loop || false,
      on: {
        slideChange: () => {
          this.slideChange.emit(this.activeIndex);
          this.cdRef.markForCheck();
        }
      },
      loopAdditionalSlides: 0,
      breakpoints: {
        0: {
          slidesPerView: config.breakpoints[BRK.S]?.slidesPerView ?? nbSlidesXS,
          spaceBetween: config.breakpoints[BRK.S]?.spaceBetween ?? 20,
          slidesPerGroup: nbSlidesXS,
          preventClicksPropagation: true,
          slideToClickedSlide: true
        },
        768: {
          slidesPerView: config.breakpoints[BRK.M]?.slidesPerView ?? nbSlidesS,
          spaceBetween: config.breakpoints[BRK.S]?.spaceBetween ?? 20,
          slidesPerGroup: nbSlidesS,
          slideToClickedSlide: false,
          shortSwipes: false,
          longSwipes: false,
          simulateTouch: false
        },
        1280: {
          slidesPerView: config.breakpoints[BRK.L]?.slidesPerView ?? ngSlidesL,
          spaceBetween: config.breakpoints[BRK.S]?.spaceBetween ?? 28,
          slidesPerGroup: ngSlidesL
        }
      },
      keyboard: {
        enabled: true
      },
      a11y: {
        enabled: false
      }
    });

    this.cdRef.detectChanges();
  }

  /**
   * Compute slider breakpoints sizes
   * @param size The slider size
   * @returns Returns [L, S, XS] breakpoints sizes
   */
  private getSliderSize(size: SizesEnum): [number, number, number] {
    switch (size) {
      case SizesEnum.l:
        return [1, 1, 1];
      case SizesEnum.m:
        return [2, 2, 1];
      case SizesEnum.s:
        return [3, 2, 1];
      case SizesEnum.xs:
        return [4, 2, 1];
      case SizesEnum.xxs:
        return [6, 3, 1];
      default:
        return [1, 1, 1];
    }
  }

  private updateButtons(): void {
    this.isPrevBtnDisabled.set(!!this.buttonPrev()?.nativeElement?.classList.contains('is-disabled'));
    this.isNextBtnDisabled.set(!!this.buttonNext()?.nativeElement?.classList.contains('is-disabled'));
  }
}
